// BF5d-2: the shipment whose freight cannot be split as configured.
//
// WHAT THIS IS FOR. A By-Weight or By-Volume shipment whose assigned lines all
// carry a zero dimension gives the allocator a denominator of zero, and the
// engine refuses it outright rather than dividing by something meaningless.
// That refusal fails the WHOLE save, taking every line edit in the same request
// with it -- and on the draft autosave path it does so with nothing on screen
// at all, every 1.5s. This banner is what makes the blocked save visible.
//
// ⚠️ THIS IS NOT A SAVE RESULT, which is why it is not SaveQuoteBanner -- the
// same distinction QuoteFinalisedBanner draws, and for the same reason. That
// component renders off `saveStatus`, the outcome of a BUTTON PRESS, so driving
// it from a blocked autosave would print "Not saved." for a press nobody made.
// This one describes the state of the QUOTE, so it stays up while the condition
// holds regardless of whether anyone has pressed anything.
//
// ⚠️ DERIVED, NOT LATCHED -- and this is where it deliberately differs from
// QuoteFinalisedBanner. Finalising is a one-way door, so that banner latches and
// never clears. This condition is the opposite: changing the basis or assigning
// a line that carries the dimension fixes it, and the banner must go away by
// itself when it does. So QuoteDetail recomputes the problems on every render
// rather than setting a flag, and "it clears when fixed" is a property of the
// structure instead of something a later edit has to remember to do.
//
// ⚠️ THE REMEDY IT OFFERS HAS TO BE ONE THAT EXISTS. The grid has no weight or
// volume column -- those come from the catalog when a product is picked and are
// editable nowhere -- so telling someone to fill in the weight would send them
// hunting for a control that is not there. The message (built in
// services/freightBasisGuard.ts, one copy for both save seams and this banner)
// names the two things a person can actually do: change the basis in the
// sourcing panel, or assign lines that carry the dimension.

import { AlertTriangle } from "lucide-react";
import {
  semanticColors, radius, spacing, typography, iconSize, iconStroke,
} from "@/styles/tokens";

export function FreightBasisBanner({
  // The one sentence, already built. Passed in rather than derived here so the
  // banner and the two save seams cannot word the same refusal differently --
  // freightBasisMessage owns it.
  message,
}: {
  message: string;
}) {
  if (!message) return null;

  return (
    <div
      data-testid="quote-freight-basis-banner"
      className="flex items-start"
      style={{
        gap: spacing[8],
        marginTop: spacing[12],
        padding: `${spacing[8]}px ${spacing[12]}px`,
        backgroundColor: semanticColors.warningBg,
        border: `1px solid ${semanticColors.warningBorder}`,
        borderRadius: radius.base,
        fontSize: typography.fontSize.sm,
        color: semanticColors.warningTextOnTint,
      }}
    >
      <AlertTriangle
        size={iconSize.md}
        strokeWidth={iconStroke.regular}
        style={{ flexShrink: 0, marginTop: spacing[2], color: semanticColors.warning }}
      />
      <span>
        {/* It says the quote is not being saved, because it is not. There is no
            reassuring wording available here that would also be true: both save
            paths decline to send while this holds. */}
        <strong>This quote is not being saved.</strong>{" "}
        <span data-testid="quote-freight-basis-detail">{message}</span>
      </span>
    </div>
  );
}
